import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  TbAlignLeft,
  TbArrowsHorizontal,
  TbChevronDown,
  TbChevronUp,
  TbCircle,
  TbCircleDot,
  TbCopy,
  TbGripVertical,
  TbPlus,
  TbSquare,
  TbSquareCheck,
  TbTrash,
  TbX,
} from 'react-icons/tb';
import {
  IQuestion,
  IQuestionOption,
  InputType,
  QUESTION_INPUT_OPTION,
} from 'types/forms';
import { ITEM_TYPE, MAX_OPTIONS, createOptions } from './constants';
import * as S from './style';

const TYPE_ICON: Record<InputType, React.ReactNode> = {
  SINGLE_CHOICE: <TbCircleDot size={16} />,
  MULTIPLE_CHOICE: <TbSquareCheck size={16} />,
  LINEAR_SCALE: <TbArrowsHorizontal size={16} />,
  TEXT: <TbAlignLeft size={16} />,
};

const MIN_RANGE = [0, 1];
const MAX_RANGE = [2, 3, 4, 5, 6, 7, 8, 9, 10];

interface DragItem {
  id: number;
  index: number;
}

interface Props {
  question: IQuestion;
  index: number;
  total: number;
  autoFocus: boolean;
  onChange: (question: IQuestion) => void;
  onRemove: (id: number) => void;
  onDuplicate: (id: number) => void;
  onMove: (from: number, to: number) => void;
}

function Question({
  question,
  index,
  total,
  autoFocus,
  onChange,
  onRemove,
  onDuplicate,
  onMove,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLSpanElement>(null);
  const [focusOptionId, setFocusOptionId] = useState<number | null>(null);
  const options = question.options || [];

  const [, drop] = useDrop<DragItem>({
    accept: ITEM_TYPE,
    hover(item, monitor) {
      if (!ref.current || item.index === index) return;

      const rect = ref.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;
      const offset = monitor.getClientOffset();
      if (!offset) return;
      const hoverY = offset.y - rect.top;

      if (item.index < index && hoverY < middleY) return;
      if (item.index > index && hoverY > middleY) return;

      onMove(item.index, index);
      item.index = index;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: (): DragItem => ({ id: question.id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(handleRef);
  preview(drop(ref));

  const update = (patch: Partial<IQuestion>) =>
    onChange({ ...question, ...patch });

  const updateOption = (idx: number, patch: Partial<IQuestionOption>) =>
    update({
      options: options.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    });

  const handleChangeType = (type: InputType) => {
    if (type === question.type) return;

    const isChoice = (t: InputType) =>
      t === 'SINGLE_CHOICE' || t === 'MULTIPLE_CHOICE';
    const keep = isChoice(type) && isChoice(question.type) && options.length;

    update({ type, options: keep ? options : createOptions(type) });
  };

  const handleAddOption = () => {
    if (options.length >= MAX_OPTIONS) return;

    const id = Math.max(0, ...options.map((o) => o.id)) + 1;
    update({
      options: [...options, { id, value: `옵션 ${options.length + 1}` }],
    });
    setFocusOptionId(id);
  };

  const handleRemoveOption = (idx: number) =>
    update({ options: options.filter((_, i) => i !== idx) });

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    e.preventDefault();
    handleAddOption();
  };

  const renderChoice = () => (
    <div css={S.optionList}>
      {options.map((option, idx) => (
        <div key={option.id} css={S.optionRow}>
          <span css={S.optionMarker}>
            {question.type === 'MULTIPLE_CHOICE' ? (
              <TbSquare size={20} />
            ) : (
              <TbCircle size={20} />
            )}
          </span>
          <input
            css={S.optionInput}
            value={option.value}
            placeholder={`옵션 ${idx + 1}`}
            autoFocus={option.id === focusOptionId}
            onFocus={(e) => option.id === focusOptionId && e.target.select()}
            onChange={(e) => updateOption(idx, { value: e.target.value })}
            onKeyDown={handleOptionKeyDown}
          />
          <button
            type="button"
            css={S.iconButton}
            disabled={options.length <= 1}
            onClick={() => handleRemoveOption(idx)}
            aria-label="옵션 삭제"
          >
            <TbX size={18} />
          </button>
        </div>
      ))}
      {options.length < MAX_OPTIONS ? (
        <button type="button" css={S.addOption} onClick={handleAddOption}>
          <TbPlus size={16} />
          옵션 추가
        </button>
      ) : (
        <p css={[S.titleHint, { marginLeft: 30 }]}>
          옵션은 최대 {MAX_OPTIONS}개까지 추가할 수 있어요.
        </p>
      )}
    </div>
  );

  const renderScale = () => {
    const last = options.length - 1;
    const min = options[0]?.subValue ?? '1';
    const max = options[last]?.subValue ?? '5';
    const hasMiddle = options.length > 2;

    const labelOf = (idx: number) => {
      if (idx === 0) return `${min}점`;
      if (idx === last) return `${max}점`;
      return '가운데';
    };

    const placeholderOf = (idx: number) => {
      if (idx === 0) return '예: 전혀 그렇지 않다';
      if (idx === last) return '예: 매우 그렇다';
      return '예: 보통이다';
    };

    const toggleMiddle = () => {
      if (hasMiddle) {
        update({ options: [options[0], options[last]] });
        return;
      }
      const id = Math.max(0, ...options.map((o) => o.id)) + 1;
      update({ options: [options[0], { id, value: '' }, options[last]] });
      setFocusOptionId(id);
    };

    return (
      <div css={S.optionList}>
        <div css={S.scaleRange}>
          <select
            value={min}
            onChange={(e) => updateOption(0, { subValue: e.target.value })}
            aria-label="최소값"
          >
            {MIN_RANGE.map((v) => (
              <option key={v} value={String(v)}>
                {v}
              </option>
            ))}
          </select>
          <span>부터</span>
          <select
            value={max}
            onChange={(e) => updateOption(last, { subValue: e.target.value })}
            aria-label="최대값"
          >
            {MAX_RANGE.map((v) => (
              <option key={v} value={String(v)}>
                {v}
              </option>
            ))}
          </select>
          <span>까지</span>
        </div>
        {options.map((option, idx) => (
          <div key={option.id} css={S.optionRow}>
            <span css={S.scaleLabel}>{labelOf(idx)}</span>
            <input
              css={S.optionInput}
              value={option.value}
              placeholder={`${placeholderOf(idx)} (선택)`}
              autoFocus={option.id === focusOptionId}
              onChange={(e) => updateOption(idx, { value: e.target.value })}
            />
          </div>
        ))}
        <button
          type="button"
          css={[S.addOption, { marginLeft: 58 }]}
          onClick={toggleMiddle}
        >
          {hasMiddle ? <TbX size={16} /> : <TbPlus size={16} />}
          {hasMiddle ? '가운데 라벨 삭제' : '가운데 라벨 추가'}
        </button>
      </div>
    );
  };

  return (
    <div
      ref={ref}
      css={[S.card(false), { opacity: isDragging ? 0.4 : 1 }]}
      data-question-id={question.id}
    >
      <div css={S.questionHead}>
        <span ref={handleRef} css={S.handle} title="드래그해서 순서 변경">
          <TbGripVertical size={18} />
        </span>
        <span css={S.questionNo}>
          질문 {index + 1}
          {question.isRequired && <span css={S.requiredMark}>*</span>}
        </span>
        <div css={S.moveButtons}>
          <button
            type="button"
            css={S.iconButton}
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
            aria-label="위로 이동"
          >
            <TbChevronUp size={20} />
          </button>
          <button
            type="button"
            css={S.iconButton}
            disabled={index === total - 1}
            onClick={() => onMove(index, index + 1)}
            aria-label="아래로 이동"
          >
            <TbChevronDown size={20} />
          </button>
        </div>
      </div>

      <div css={S.chips} role="radiogroup" aria-label="질문 유형">
        {QUESTION_INPUT_OPTION.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={question.type === opt.value}
            css={S.chip(question.type === opt.value)}
            onClick={() => handleChangeType(opt.value)}
          >
            {TYPE_ICON[opt.value]}
            {opt.name}
          </button>
        ))}
      </div>

      <input
        type="text"
        css={S.questionTitle}
        placeholder="질문을 입력해주세요"
        value={question.title}
        autoFocus={autoFocus}
        onChange={(e) => update({ title: e.target.value })}
      />
      <textarea
        css={S.description}
        rows={2}
        placeholder="설명을 덧붙일 수 있어요 (선택)"
        value={question.description}
        onChange={(e) => update({ description: e.target.value })}
      />

      {(question.type === 'SINGLE_CHOICE' ||
        question.type === 'MULTIPLE_CHOICE') &&
        renderChoice()}
      {question.type === 'LINEAR_SCALE' && renderScale()}
      {question.type === 'TEXT' && (
        <div css={S.textPreview}>응답자가 직접 답변을 입력해요</div>
      )}

      <div css={S.questionFoot}>
        <button
          type="button"
          css={S.iconButton}
          onClick={() => onDuplicate(question.id)}
          aria-label="질문 복제"
          title="복제"
        >
          <TbCopy size={20} />
        </button>
        <button
          type="button"
          css={S.iconButton}
          onClick={() => onRemove(question.id)}
          aria-label="질문 삭제"
          title="삭제"
        >
          <TbTrash size={20} />
        </button>
        <span css={S.divider} />
        <label css={S.toggle}>
          필수
          <input
            type="checkbox"
            checked={!!question.isRequired}
            onChange={(e) => update({ isRequired: e.target.checked })}
          />
        </label>
      </div>
    </div>
  );
}

export default Question;
